import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

const ConfirmContext = createContext(null);

/**
 * Async confirm() that replaces window.confirm().
 *
 * Usage:
 *   const confirm = useConfirm();
 *   if (await confirm({ title: '...', message: '...' })) { ... }
 *
 * Options:
 *   title       – dialog title (default: 'Confirmar')
 *   message     – body text (required)
 *   confirmText – confirm button label (default: 'Confirmar')
 *   cancelText  – cancel button label (default: 'Cancelar')
 *   color       – confirm button color (default: 'primary')
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  return ctx;
}

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState({});
  const resolveRef = useRef(null);

  const confirm = useCallback((opts) => {
    const merged = typeof opts === 'string' ? { message: opts } : opts;
    setOptions(merged);
    setOpen(true);
    return new Promise((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleClose = (result) => {
    setOpen(false);
    resolveRef.current?.(result);
    resolveRef.current = null;
  };

  const {
    title = 'Confirmar',
    message = '',
    confirmText = 'Confirmar',
    cancelText = 'Cancelar',
    color = 'primary',
  } = options;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={open}
        onClose={() => handleClose(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleClose(false)}>{cancelText}</Button>
          <Button
            onClick={() => handleClose(true)}
            variant='contained'
            color={color}
            autoFocus
          >
            {confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
